/**
 * Basic slider event handling
 * [1. Navigation buttons] [2. Pagination buttons]
 */
class SliderBasicHandler {
  constructor(slider) {
    this.slider = slider;
    this.prevButton = this.slider.querySelector('button[name="previous"]');
    this.nextButton = this.slider.querySelector('button[name="next"]');
    this.pageDotsWrapper = this.slider.querySelector(".slider-page-dots"); // Pagination container
    this.counterWrapper = this.slider.querySelector(".slider-counter"); // Counter container

    this.initModule();
  }

  /**
   * Page button click
   * @param direction Direction: previous|next
   */
  onButtonClick(direction) {
    // Assumes slider has a method to handle button clicks
    this.slider.slideByDirection(direction);
  }

  // Pagination button click
  onPageLinkClick(event) {
    const page = parseInt(event.currentTarget.dataset.page, 10);
    this.slider.slideByPage(page);
  }

  initModule() {
    // Create and bind event handler
    const onButtonClickFunction = webvista.debounce(
      this.onButtonClick.bind(this),
      200,
    );
    this.boundOnButtonClick = (event) => {
      const direction = event.currentTarget.name;
      onButtonClickFunction(direction);
    };

    // Get total page count
    this.pageCount = this.slider.slideLength;
    if (!this.slider.ifLoopingInfinite) {
      this.pageCount = this.slider.slideLength - this.slider.slidesPerPage + 1;
    }

    // Navigation buttons
    if (this.prevButton)
      this.prevButton.addEventListener("click", this.boundOnButtonClick);
    if (this.nextButton)
      this.nextButton.addEventListener("click", this.boundOnButtonClick);

    // Create pagination dots
    if (this.pageDotsWrapper) this.initPageDots();

    // Create page counter display
    if (this.counterWrapper) this.initCounter();

    // Support left/right arrow key navigation (reversed in RTL)
    this.boundKeydownHandling = (event) => {
      const key = event.code ? event.code.toUpperCase() : "";
      const isRTL = webvista.isRTL();

      if (key === "ARROWLEFT") {
        event.preventDefault();
        this.slider.slideByDirection(isRTL ? "next" : "previous");
      }

      if (key === "ARROWRIGHT") {
        event.preventDefault();
        this.slider.slideByDirection(isRTL ? "previous" : "next");
      }
    };
    this.slider.addEventListener("keydown", this.boundKeydownHandling);
  }

  /**
   * Initialize pagination dots
   */
  initPageDots() {
    if (!this.pageDotsWrapper) return;
    this.boundOnPageButtonClick = this.onPageLinkClick.bind(this);

    for (let i = 1; i <= this.pageCount; i++) {
      const link = document.createElement("li");
      link.className = "page-dot";
      link.setAttribute("data-page", i);
      link.setAttribute("tabindex", "0"); // Focusable
      link.setAttribute("role", "button");
      link.setAttribute(
        "aria-label",
        `${this.pageDotsWrapper.dataset.ariaPrefix || "Go to slide"} ${i}`,
      );
      if (this.pageDotsWrapper.hasAttribute("data-number"))
        link.innerText = i.toString();

      link.addEventListener("click", this.boundOnPageButtonClick);
      // Add keyboard events
      link.addEventListener("keydown", (event) => {
        const key = event.code ? event.code.toUpperCase() : "";

        if (key === "ENTER" || key === "SPACE") {
          event.preventDefault(); // Prevent page scroll (default space key behavior)

          this.boundOnPageButtonClick(event);
        }
      });

      this.pageDotsWrapper.appendChild(link);
    }
  }

  /**
   * Initialize page counter
   */
  initCounter() {
    const totalCounterElement =
      this.counterWrapper.querySelector(".total-page");
    if (totalCounterElement) totalCounterElement.innerText = this.pageCount;
  }

  /**
   * Update basic module info after page change
   * @param currentSlide Current slide index
   * @param totalPage Total number of pages
   */
  updateInfo(currentSlide = null, totalPage = 1) {
    if (!currentSlide) return;

    const currentPage = parseInt(currentSlide.dataset.page, 10);

    // Pagination
    if (this.pageDotsWrapper) {
      const currentDot = this.pageDotsWrapper.querySelector("li.current");
      if (currentDot) {
        currentDot.classList.remove("current");
        currentDot.removeAttribute("aria-current");
      }
      const newDot = this.pageDotsWrapper.querySelector(
        `li[data-page="${currentPage}"]`,
      );
      if (newDot) {
        newDot.classList.add("current");
        newDot.setAttribute("aria-current", "true");
      }
    }

    // Current page
    if (this.counterWrapper) {
      const currentCounterElement =
        this.counterWrapper.querySelector(".current-page");
      if (currentCounterElement) {
        currentCounterElement.innerText = currentPage;
      }
    }

    // Navigation buttons
    if (!this.slider.ifEnableLooping) {
      this.prevButton &&
        this.prevButton.toggleAttribute("disabled", currentPage <= 1);
      this.nextButton &&
        this.nextButton.toggleAttribute("disabled", currentPage >= totalPage);
    }
  }

  /**
   * Unmount module
   */
  unmount() {
    if (this.prevButton)
      this.prevButton.removeEventListener("click", this.boundOnButtonClick);
    if (this.nextButton)
      this.nextButton.removeEventListener("click", this.boundOnButtonClick);

    if (this.pageDotsWrapper) this.pageDotsWrapper.innerHTML = "";

    if (this.boundKeydownHandling)
      this.slider.removeEventListener("keydown", this.boundKeydownHandling);
  }
}

/**
 * SliderAutoplay manages the autoplay functionality of the slider.
 * It starts or pauses playback based on slider visibility and user interaction
 * (e.g., mouse hover or focus events). It also uses Intersection Observer API
 * to detect whether the slider is visible in the viewport and controls autoplay accordingly.
 */
class SliderAutoplay {
  /**
   * Constructor: Initialize the autoplay class.
   * @param {Slider} slider - Reference to the slider.
   * @param {number} interval - Autoplay interval duration.
   * @param {boolean} reverse Whether to play in reverse, default: false
   * @param focusPause Whether to pause playback on focus
   */
  constructor(slider, interval, reverse = false, focusPause = false) {
    this.slider = slider;
    this.interval = interval;
    this.autoInterval = null;
    this.direction = reverse ? "previous" : "next";
    this.focusPause = focusPause;
    this.viewportPaused = false;
    this.visibilityObserver = null;

    this.toggleAutoplayButton = this.slider.querySelector(
      ".slider-toggle-autoplay-button",
    );

    this.ifPaused = false; // Whether playback is paused, used to control play state
    this.startTime = null; // Start time of the current slide cycle
    this.spendTime = null; // Elapsed time of current cycle when paused, used for resuming
    this.leftTime = this.interval; // Remaining time when resuming playback

    this.initMount();
  }

  /**
   * Play
   * @param refresh Reset playback
   */
  play(refresh = false) {
    if (refresh) {
      if (this.autoInterval) clearTimeout(this.autoInterval);
      this.startTime = null;
      this.leftTime = this.interval;
    }
    this.ifPaused = false;
    if (this.viewportPaused) return;
    this.slider.classList.add("slider--is-playing"); // slider--is-playing controls pause/resume state
    this.#doPlay();
  }

  /**
   * Execute playback
   */
  #doPlay() {
    if (this.slider.hasAttribute("editor-selected") || this.ifPaused) return;

    this.startTime = Date.now(); // Record cycle start time
    this.autoInterval = setTimeout(() => {
      this.slider.slideByDirection(this.direction);

      this.leftTime = this.interval; // Required to prevent unhandled slideChange events in very short intervals
    }, this.leftTime);
  }

  /**
   * Pause playback
   */
  pause(setUserPaused = true) {
    if (setUserPaused) this.ifPaused = true;

    if (this.autoInterval) {
      clearTimeout(this.autoInterval);
      this.autoInterval = null;

      // If a cycle has started, calculate elapsed time.
      // Then compute the remaining time for resuming playback
      if (this.startTime) {
        this.spendTime = Date.now() - this.startTime;
        this.leftTime = Math.max(0, this.leftTime - this.spendTime);
      }
    }

    this.slider.classList.remove("slider--is-playing"); // Pause playback
  }

  /**
   * Toggle between pause and resume via button
   */
  toggleAutoplayHandling() {
    this.ifPaused ? this.play() : this.pause();
  }

  /**
   * Triggered when the slider gains focus or mouse hover; pauses autoplay.
   */
  focusInHandling() {
    this.pause(false); // Pause autoplay without marking as user-paused
  }

  /**
   * Triggered when the slider loses focus or mouse leaves; resumes autoplay.
   */
  focusOutHandling() {
    if (!this.ifPaused) this.play(); // Only resume if not user-paused
  }

  /**
   * Add event listeners to handle focus and mouse events.
   */
  initMount() {
    // Bind event handlers
    if (this.focusPause) {
      this.boundFocusInHandling = this.focusInHandling.bind(this);
      this.boundFocusOutHandling = this.focusOutHandling.bind(this);
      this.slider.addEventListener("mouseover", this.boundFocusInHandling);
      this.slider.addEventListener("mouseleave", this.boundFocusOutHandling);
      this.slider.addEventListener("focusin", this.boundFocusInHandling);
      this.slider.addEventListener("focusout", this.boundFocusOutHandling);
    }

    if (this.toggleAutoplayButton) {
      this.boundToggleHandling = this.toggleAutoplayHandling.bind(this);
      this.toggleAutoplayButton.addEventListener(
        "click",
        this.boundToggleHandling,
      );
    }

    // Use Intersection Observer to monitor slider visibility
    if ("IntersectionObserver" in window) {
      this.visibilityObserver = new IntersectionObserver((entries) => {
        for (const entry of entries) {
          if (entry.target !== this.slider) continue;
          if (entry.isIntersecting) {
            // Entered viewport, resume playback
            this.viewportPaused = false;
            if (!this.ifPaused) this.play();
          } else {
            // Left viewport, pause playback
            if (this.viewportPaused) return;
            this.viewportPaused = true;
            this.pause(false);
          }
        }
      });
      this.visibilityObserver.observe(this.slider);
    }
  }

  /**
   * Unmount module.
   */
  unmount() {
    if (this.focusPause) {
      this.slider.removeEventListener("mouseover", this.boundFocusInHandling);
      this.slider.removeEventListener("mouseleave", this.boundFocusOutHandling);
      this.slider.removeEventListener("focusin", this.boundFocusInHandling);
      this.slider.removeEventListener("focusout", this.boundFocusOutHandling);
    }

    if (this.toggleAutoplayButton)
      this.toggleAutoplayButton.removeEventListener(
        "click",
        this.boundToggleHandling,
      );

    if (this.visibilityObserver) {
      this.visibilityObserver.disconnect();
      this.visibilityObserver = null;
    }
  }
}

/**
 * SliderDrag handles the drag functionality of the slider.
 * It allows users to switch slides via mouse or touch dragging.
 */
class SliderDrag {
  /**
   * Constructor: Initialize drag functionality.
   * @param {Slider} slider - Reference to the slider.
   * @param {Boolean} smooth Whether to enable smooth scrolling; if false, dragging has no animation
   */
  constructor(slider, smooth = true) {
    this.DRAG_MOVE_RATE = 1.5; // Drag movement rate

    this.slider = slider;
    this.smooth = smooth;
    this.isClicking = false; // Currently clicking
    this.isDragging = false; // Currently dragging
    this.dragStartPos = null;

    this.boundDragStart = this.onDragStart.bind(this);
    this.boundDragMove = this.onDragMove.bind(this);
    this.boundDragEnd = this.onDragEnd.bind(this);

    // Bind event listeners
    this.bindEventListener();
  }

  /**
   * Handle drag start event.
   * @param {MouseEvent|TouchEvent} event - The event object that triggered the drag.
   */
  onDragStart(event) {
    document.addEventListener("mousemove", this.boundDragMove, {
      passive: true,
    });
    document.addEventListener("mouseup", this.boundDragEnd, { passive: true });

    document.addEventListener("touchmove", this.boundDragMove, {
      passive: false,
    });
    document.addEventListener("touchend", this.boundDragEnd, { passive: true });

    this.isClicking = true;
    this.isDragging = false;
    this.dragDirectionLocked = false; // Whether the drag direction has been determined
    this.dragAllowed = true; // Whether dragging is allowed (matches slider axis)

    if (event["touches"]) {
      this.dragStartPos = this.slider.ifLayoutVertical
        ? event["touches"][0].clientY
        : event["touches"][0].clientX;
      this.dragStartX = event["touches"][0].clientX;
      this.dragStartY = event["touches"][0].clientY;
    } else {
      this.dragStartPos = this.slider.ifLayoutVertical
        ? event.clientY
        : event.clientX;
      this.dragStartX = event.clientX;
      this.dragStartY = event.clientY;
    }

    this.slider.preTranslate = this.slider.currentTranslate;
    this.slider.disableTransition(); // Disable transition
  }

  /**
   * Handle drag move event.
   * @param {MouseEvent|TouchEvent} event - The event object that triggered the move.
   */
  onDragMove(event) {
    if (!this.isClicking) return;

    // Determine drag direction on first significant movement (touch only)
    if (event["touches"] && !this.dragDirectionLocked) {
      const dx = Math.abs(event["touches"][0].clientX - this.dragStartX);
      const dy = Math.abs(event["touches"][0].clientY - this.dragStartY);
      const LOCK_THRESHOLD = 5;

      if (dx > LOCK_THRESHOLD || dy > LOCK_THRESHOLD) {
        this.dragDirectionLocked = true;
        // Horizontal slider: allow only if primarily horizontal; vertical slider: allow only if primarily vertical
        this.dragAllowed = this.slider.ifLayoutVertical ? (dy > dx) : (dx > dy);
      } else {
        return; // Not enough movement to determine direction
      }
    }

    // If drag direction doesn't match slider axis, abort dragging and let page scroll
    if (!this.dragAllowed) {
      this.isClicking = false;
      this.slider.enableTransition();
      document.removeEventListener("mousemove", this.boundDragMove);
      document.removeEventListener("mouseup", this.boundDragEnd);
      document.removeEventListener("touchmove", this.boundDragMove);
      document.removeEventListener("touchend", this.boundDragEnd);
      return;
    }

    let currentPos;
    if (event["touches"]) {
      currentPos = this.slider.ifLayoutVertical
        ? event["touches"][0].clientY
        : event["touches"][0].clientX;
    } else {
      currentPos = this.slider.ifLayoutVertical ? event.clientY : event.clientX;
    }

    const dragOffset = this.dragStartPos - currentPos;
    if (Math.abs(dragOffset) > 1) {
      if (event["touches"] && event.cancelable) event.preventDefault();
      this.isDragging = true;
      this.slider.classList.add("slider--is-dragging");

      // If an animation frame exists, cancel the previous request
      if (this.rafId) cancelAnimationFrame(this.rafId);

      if (!this.slider.ifLayoutVertical && webvista.isRTL()) {
        // Horizontal RTL
        this.slider.currentTranslate =
          this.slider.preTranslate - dragOffset * this.DRAG_MOVE_RATE;
      } else {
        this.slider.currentTranslate =
          this.slider.preTranslate + dragOffset * this.DRAG_MOVE_RATE;
      }

      if (this.smooth) {
        // Execute move on next animation frame
        this.rafId = requestAnimationFrame(() => {
          this.slider.applyTranslation(); // Update element position
        });
      }
    }
  }

  /**
   * Handle drag end event.
   * @param {MouseEvent|TouchEvent} event - The event object that triggered drag end.
   */
  onDragEnd(event) {
    document.removeEventListener("mousemove", this.boundDragMove);
    document.removeEventListener("mouseup", this.boundDragEnd);

    document.removeEventListener("touchmove", this.boundDragMove);
    document.removeEventListener("touchend", this.boundDragEnd);

    this.isClicking = false;
    this.slider.enableTransition(); // Restore transition

    // No movement
    if (!this.isDragging) return;
    this.isDragging = false;

    event.stopPropagation();

    this.slider.classList.remove("slider--is-dragging");

    // Block one click event after drag to prevent accidental link navigation
    const preventClick = (e) => {
      e.preventDefault();
      e.stopPropagation();
      clearTimeout(preventClickTimer);
    };
    this.slider.addEventListener("click", preventClick, {
      capture: true,
      once: true,
    });
    // Safety: remove listener if no click fires within 500ms (e.g., touch-only devices)
    const preventClickTimer = setTimeout(() => {
      this.slider.removeEventListener("click", preventClick, { capture: true });
    }, 500);

    // Cancel ongoing animation
    if (this.rafId) cancelAnimationFrame(this.rafId);

    if (this.slider.currentTranslate !== this.slider.preTranslate) {
      this.updateIndexAfterDrag();
      this.slider.performSlide(); // Perform slide based on new index
    }
  }

  /**
   * Update the slider index after drag ends.
   * Handles boundary conditions and auto-corrects if needed.
   */
  updateIndexAfterDrag() {
    const movedBy = this.slider.currentTranslate - this.slider.preTranslate;
    const DRAG_THRESHOLD = Math.min(
      this.slider.sliderItemOffset / 2,
      this.slider.TRANSITION_THRESHOLD,
    );

    if (movedBy < 0) {
      // Moved left-to-right or downward
      this.slider.index = Math.floor(
        (this.slider.currentTranslate + DRAG_THRESHOLD) /
          this.slider.sliderItemOffset,
      );
    } else if (movedBy > 0) {
      // Moved right-to-left or upward
      this.slider.index = Math.ceil(
        (this.slider.currentTranslate - DRAG_THRESHOLD) /
          this.slider.sliderItemOffset,
      );
    }
  }

  /**
   * Add drag event listeners to the slider.
   */
  bindEventListener() {
    this.slider.sliderContainer.addEventListener(
      "mousedown",
      this.boundDragStart,
      { passive: true },
    );
    this.slider.sliderContainer.addEventListener(
      "touchstart",
      this.boundDragStart,
      { passive: true },
    );
  }

  /**
   * Unmount module.
   */
  unmount() {
    // Remove mouse events
    this.slider.sliderContainer.removeEventListener(
      "mousedown",
      this.boundDragStart,
    );
    document.removeEventListener("mousemove", this.boundDragMove);
    document.removeEventListener("mouseup", this.boundDragEnd);

    // Remove touch events
    this.slider.sliderContainer.removeEventListener(
      "touchstart",
      this.boundDragStart,
    );
    document.removeEventListener("touchmove", this.boundDragMove);
    document.removeEventListener("touchend", this.boundDragEnd);
  }
}

/**
 * SliderWheel handles scroll wheel and trackpad sliding for the slider.
 * Supports mouse wheel (deltaY) and Mac trackpad horizontal swipe (deltaX).
 */
class SliderWheel {
  /**
   * Constructor: Initialize wheel functionality.
   * @param {Slider} slider - Reference to the slider.
   */
  constructor(slider) {
    this.slider = slider;
    this.SCROLL_THRESHOLD = 30; // Scroll threshold (compatible with small trackpad delta values)
    this.WHEEL_COOLDOWN = 500; // Cooldown time (ms) to prevent continuous paging from trackpad inertia
    this._wheelDelta = 0;
    this._wheelCooldown = false;

    this.boundHandleWheel = this.handleWheel.bind(this);

    this.bindEventListener();
  }

  handleWheel(event) {
    // Select scroll axis based on slider direction
    let delta;
    if (this.slider.ifLayoutVertical) {
      if (!this.slider.ifEnableWheeling) return;
      // Vertical slider: use deltaY
      delta = event.deltaY;
    } else if (Math.abs(event.deltaX) > Math.abs(event.deltaY)) {
      // Primarily horizontal scroll (trackpad swipe), reversed in RTL
      delta = webvista.isRTL() ? -event.deltaX : event.deltaX;
    } else if (this.slider.ifEnableWheeling) {
      // Primarily vertical scroll (mouse wheel for horizontal slider)
      delta = event.deltaY;
    } else {
      return;
    }

    // Delta is 0, skip
    if (delta === 0) return;

    // Check if scrolling in this direction is possible
    const canForward =
      this.slider.ifEnableLooping || this.slider.index < this.slider.totalPages - 1;
    const canBackward = this.slider.ifEnableLooping || this.slider.index > 0;
    if ((delta > 0 && !canForward) || (delta < 0 && !canBackward)) return;

    // Prevent default behavior and event bubbling
    event.stopPropagation();
    event.preventDefault();

    // In cooldown, ignore subsequent scrolls (including trackpad inertia)
    if (this._wheelCooldown) return;

    this._wheelDelta += delta;

    // Below threshold
    if (Math.abs(this._wheelDelta) < this.SCROLL_THRESHOLD) return;

    // Change page
    if (this._wheelDelta > 0 && canForward) {
      this.slider.index++;
    } else if (this._wheelDelta < 0 && canBackward) {
      this.slider.index--;
    }

    this._wheelDelta = 0;

    // Trigger cooldown to prevent inertia-based continuous paging
    this._wheelCooldown = true;
    this._cooldownTimer = setTimeout(() => {
      this._wheelCooldown = false;
    }, this.WHEEL_COOLDOWN);

    this.slider.performSlide();
  }

  bindEventListener() {
    this.slider.addEventListener("wheel", this.boundHandleWheel, {
      passive: false,
    });
  }

  /**
   * Unmount module
   */
  unmount() {
    this.slider.removeEventListener("wheel", this.boundHandleWheel);
    if (this._cooldownTimer) clearTimeout(this._cooldownTimer);
    this._wheelDelta = 0;
  }
}

/**
 * SliderInfiniteLoop handles seamless infinite loop scrolling.
 * Supports infinite dragging.
 */
class SliderInfiniteLoop {
  constructor(slider) {
    this.slider = slider;
    this.paddingSlides();

    this.boundSlideChange = this.onSlideChange.bind(this);
    this.slider.addEventListener("sliderChanged", this.boundSlideChange);
  }

  /**
   * Pad the slider with cloned slides before and after the original slides.
   * Related to the number of visible items: 2n-1 clones are needed on each side,
   * where n is the number of simultaneously visible items.
   * E.g., if 3 items are visible, 5 clones are added to the start and end.
   */
  paddingSlides() {
    const {
      slidesPerPage,
      slideLength: totalSlides,
    } = this.slider;
    const paddingCount = 2 * slidesPerPage - 1;

    // Create document fragments
    const fragmentAfter = document.createDocumentFragment();
    const fragmentBefore = document.createDocumentFragment();
    for (let i = 0; i < paddingCount; i++) {
      // Clone slide and append to document fragment
      let cloneSlideItem = this.cloneSlide(i % totalSlides);
      if (cloneSlideItem) fragmentAfter.append(cloneSlideItem);

      cloneSlideItem = this.cloneSlide(totalSlides - 1 - (i % totalSlides));
      if (cloneSlideItem) fragmentBefore.prepend(cloneSlideItem);
    }
    // Append and prepend cloned slides
    this.slider.sliderWrapper.append(fragmentAfter);
    this.slider.sliderWrapper.prepend(fragmentBefore);

    this.slider.updateSliderMetrics(); // Update slider metrics
    this.slider.index = paddingCount; // Reposition
    if (typeof initializeScrollAnimationTrigger === "function") {
      initializeScrollAnimationTrigger(); // Reinitialize animation listeners
    }

    webvista.initLazyImages(); // Initialize lazy image loading
    webvista.initTooltips();
  }

  /**
   * Clone a slide item
   * @param fromIndex
   * @returns {Node|null}
   */
  cloneSlide(fromIndex = null) {
    const sourceSlide = this.slider.sliderItems[fromIndex];
    if (!sourceSlide) return null;
    const cloneSlide = sourceSlide.cloneNode(true);

    cloneSlide.classList.add("slider-slide--clone");
    cloneSlide.setAttribute("aria-hidden", true); // Hide from assistive devices
    cloneSlide.setAttribute("data-clone-from", `#${cloneSlide.id}`);
    cloneSlide.setAttribute("data-page", fromIndex + 1);

    // Ensure unique clone IDs to avoid conflicts when cloning the same source multiple times
    if (!this._cloneCounter) this._cloneCounter = 0;
    this._cloneCounter++;
    cloneSlide.id = `${cloneSlide.id}-Clone-${this._cloneCounter}`;

    // Remove Shopify editor attribute
    cloneSlide.removeAttribute("data-shopify-editor-block");

    // Remove tabindex to prevent focus (focus should stay on the original slides)
    cloneSlide.removeAttribute("tabindex");
    const elements = webvista.getFocusableElements(cloneSlide, false);

    if (elements && elements.length > 0) {
      elements.forEach((element) => {
        element.setAttribute("tabindex", -1);
      });
    }

    return cloneSlide;
  }

  /**
   * Remove padded clone slides
   */
  removePaddings() {
    this.slider
      .querySelectorAll(".slider-slide--clone")
      .forEach((element) => element.remove());
  }

  /**
   * Handle seamless loop transition
   * @param event
   */
  onSlideChange(event) {
    if (event.target !== this.slider) return; // For nested sliders, only handle events from the current slider

    const currentSlide = event.detail.currentElement;
    if (currentSlide && currentSlide.hasAttribute("data-clone-from")) {
      const cloneFromId = currentSlide.dataset.cloneFrom;

      if (cloneFromId) {
        if (this._jumpTimer) clearTimeout(this._jumpTimer);
        this._jumpTimer = setTimeout(() => {
          // Delay to prevent white flash
          this.slider.slideById(cloneFromId, true);
          this._jumpTimer = null;
        }, 30);
      }
    }
  }

  /**
   * Unmount module
   */
  unmount() {
    if (this._jumpTimer) {
      clearTimeout(this._jumpTimer);
      this._jumpTimer = null;
    }
    this.removePaddings();
    if (this.boundSlideChange)
      this.slider.removeEventListener("sliderChanged", this.boundSlideChange);
  }
}

/**
 * Base slider class
 * Supports [autoplay] [looping] [infinite loop] [draggable] [wheelable] [vertical layout]
 * Lifecycle hooks for subclass overrides: hookBeforeInstall, hookAfterInstall
 */
class Slider extends HTMLElement {
  constructor() {
    super();
    // Configuration constants
    this.TRANSITION_DURING = 300; // Transition duration
    this.DEFAULT_INTERVAL = 5000; // Slide interval
    this.TRANSITION_THRESHOLD = 50; // Boundary threshold for dragging beyond limits

    // State parameters
    this.index = 0; // Current index
    this.initSliderStatus = false; // Whether slider initialization is complete
    this.preTranslate = 0; // Previous position
    this.currentTranslate = 0; // Current position

    this.lastWindowSize = window.innerWidth;

    this.sliderContainer = this.querySelector(".slider-container");
    this.sliderWrapper = this.querySelector(".slider-wrapper");
    if (!this.sliderContainer || !this.sliderWrapper) return;

    // Accessibility attributes
    if (!this.hasAttribute("tabindex")) this.setAttribute("tabindex", "0");
    if (!this.hasAttribute("role")) this.setAttribute("role", "region");
    if (!this.hasAttribute("aria-roledescription"))
      this.setAttribute("aria-roledescription", "carousel");
    if (!this.hasAttribute("aria-label") && this.dataset.ariaLabel)
      this.setAttribute("aria-label", this.dataset.ariaLabel);

    // Initialize base configuration
    this.initConfig();

    // Add screen size change listener
    if (window.Shopify.designMode || window.debug) {
      this.debounceWindowSizeChangeHandler = webvista.debounce(
        this.onWindowSizeChange.bind(this),
        500,
      );
      window.addEventListener("resize", this.debounceWindowSizeChangeHandler);
    } else if ("ResizeObserver" in window) {
      // Production: observe container size changes (e.g., device rotation)
      this.resizeObserver = new ResizeObserver(
        webvista.debounce(() => {
          const currentSize = window.innerWidth;
          if (currentSize !== this.lastWindowSize) {
            this.lastWindowSize = currentSize;
            this.reInstall();
          }
        }, 800),
      );
      this.resizeObserver.observe(this.sliderContainer);
    }

    const observer = new IntersectionObserver(
      (entries, obs) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            this.install();
            obs.unobserve(this);
            break;
          }
        }
      },
      { root: null, rootMargin: "100px", threshold: 0 },
    );

    observer.observe(this);
  }

  disconnectedCallback() {
    this.uninstall();

    // Remove screen size change listener
    if (this.debounceWindowSizeChangeHandler)
      window.removeEventListener(
        "resize",
        this.debounceWindowSizeChangeHandler,
      );

    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
  }

  // Install
  install() {
    if (this.initSliderStatus) return;

    // Check if slider should be enabled based on screen size (mobile/desktop)
    if (webvista.isMobileScreen() && !this.ifSlideMobile) {
      return;
    } else if (!webvista.isMobileScreen() && !this.ifSlideDesktop) {
      return;
    }

    // Lifecycle hook: execute before install
    this.hookBeforeInstall();

    // Initialize position, default 0. Note: subsequent modules may modify this value!
    this.index = 0;

    // Update slide metrics and related data
    this.updateSliderMetrics();

    // Verify slider conditions are met before mounting modules
    if (!this.verifyBeforeMountModules()) return;

    // Assign page numbers to slide elements
    this.initPages();

    // Mount feature modules
    this.mountModules();

    // Initialize slider position
    this.initPosition();

    // Complete slider initialization
    this.toggleStatus(true);

    // Lifecycle hook: execute after install
    this.hookAfterInstall();
  }

  /**
   * Hook function
   * Executed before install
   */
  hookBeforeInstall() {}

  /**
   * Hook function
   * Executed after install
   */
  hookAfterInstall() {}

  // Uninstall
  uninstall() {
    if (this.autoplayHandler) this.autoplayHandler.pause(); // Stop playback

    // Clear pending timers
    if (this._changeEventTimer) {
      clearTimeout(this._changeEventTimer);
      this._changeEventTimer = null;
    }
    if (this._playMediaTimer) {
      clearTimeout(this._playMediaTimer);
      this._playMediaTimer = null;
    }

    // Remove feature modules
    this.unmountModules();

    // Clear slider translation
    // Required: if mobile uses grid layout and desktop uses slider layout,
    // resizing from desktop to mobile would cause layout issues
    this.clearTranslation();

    // Reset slider initialization status
    this.toggleStatus(false);
  }

  // Reinstall
  reInstall() {
    if (this.initSliderStatus) this.uninstall();
    this.install();
  }

  /**
   * Initialize configuration
   * data-slide-desktop {Boolean} Enable slider on desktop
   * data-slide-mobile {Boolean} Enable slider on mobile
   * data-autoplay {Boolean} Enable autoplay
   * data-autoplay-reverse {Boolean} Reverse autoplay direction, requires autoplay enabled
   * data-interval {Int} Autoplay interval in ms
   * data-focus-pause {Boolean} Pause playback on focus
   * data-slide-smooth {Boolean} Enable smooth scroll animation
   * data-draggable {Boolean} Enable drag support
   * data-wheelable {Boolean} Enable wheel-based navigation
   * data-looping-infinite {Boolean} Enable infinite seamless loop
   * data-looping {Boolean} Enable looping, auto-enabled with infinite loop or autoplay
   * data-keep-video Keep video playback state; default pauses other media and plays current video
   */
  initConfig() {
    this.ifSlideDesktop = this.hasAttribute("data-slide-desktop"); // Enable slider on desktop
    this.ifSlideMobile = this.hasAttribute("data-slide-mobile"); // Enable slider on mobile
    this.ifEnableAutoplay = this.hasAttribute("data-autoplay"); // Enable autoplay, default: false
    this.ifReversePlay = this.hasAttribute("data-autoplay-reverse"); // Reverse autoplay direction
    this.interval = parseInt(this.dataset.interval, 10) || this.DEFAULT_INTERVAL; // Slide interval in ms
    this.ifFocusPause = this.hasAttribute("data-focus-pause"); // Pause playback on focus

    // Smooth scrolling, default: false; recommended when infinite loop is enabled
    this.ifSlideSmooth = this.hasAttribute("data-slide-smooth");
    this.ifEnableDragging = this.hasAttribute("data-draggable"); // Enable dragging, default: false
    this.ifEnableWheeling = this.hasAttribute("data-wheelable"); // Enable wheel navigation, default: false
    this.ifLoopingInfinite = this.hasAttribute("data-looping-infinite"); // Enable infinite loop, default: false
    this.ifEnableLooping =
      this.ifLoopingInfinite ||
      this.ifEnableAutoplay ||
      this.hasAttribute("data-looping"); // Enable looping, default: false

    // Accessibility related
    this.ifAriaLive = this.hasAttribute("aria-live");
    if (this.ifAriaLive && this.getAttribute("aria-live") === "off")
      this.ifAriaLive = false;

    // Video media playback
    this.keepVideo = this.hasAttribute("data-keep-video");
  }

  /**
   * Initialize page numbers
   */
  initPages() {
    this.sliderItems.forEach((sliderItem, index) => {
      sliderItem.setAttribute("data-page", index + 1);
    });
  }

  /**
   * Initialize position
   */
  initPosition() {
    this.disableTransition();
    this.performSlide();

    setTimeout(() => {
      this.enableTransition();
    }, this.TRANSITION_DURING);
  }

  /**
   * Verify if slider installation conditions are met
   * @returns {boolean}
   */
  verifyBeforeMountModules() {
    return this.sliderTotalSize > this.sliderClientSize && this.totalPages > 1;
  }

  /**
   * Handle screen size change event
   */
  onWindowSizeChange() {
    const currentSize = window.innerWidth;

    if (currentSize !== this.lastWindowSize) {
      this.reInstall();

      this.lastWindowSize = currentSize;
    }
  }

  /**
   * Toggle slider installation status
   * @param status
   */
  toggleStatus(status = false) {
    this.initSliderStatus = status;
    this.classList.toggle("slider--installed", status);
  }

  /**
   * Mount all modules
   */
  mountModules() {
    if (this.initSliderStatus) return;

    // Basic event handler, including navigation and pagination buttons
    this.BasicHandler = new SliderBasicHandler(this);

    // Initialize drag
    if (this.ifEnableDragging)
      this.dragHandler = new SliderDrag(this, this.ifSlideSmooth);

    // Initialize wheel and horizontal trackpad swipe
    if (
      (this.ifEnableWheeling || this.ifEnableDragging) &&
      !webvista.isMobileScreen()
    )
      this.wheelHandler = new SliderWheel(this);

    // Infinite loop slider
    if (this.ifLoopingInfinite)
      this.seamLoopingHandler = new SliderInfiniteLoop(this);

    // Autoplay
    if (this.ifEnableAutoplay)
      this.autoplayHandler = new SliderAutoplay(
        this,
        this.interval,
        this.ifReversePlay,
        this.ifFocusPause,
      );
  }

  /**
   * Unmount all modules
   */
  unmountModules() {
    // Remove basic event handler, including navigation and pagination buttons
    if (this.BasicHandler) this.BasicHandler.unmount();

    if (this.ifEnableDragging && this.dragHandler) this.dragHandler.unmount();

    if (this.wheelHandler) this.wheelHandler.unmount();

    if (this.ifEnableAutoplay && this.autoplayHandler)
      this.autoplayHandler.unmount();

    if (this.ifLoopingInfinite && this.seamLoopingHandler)
      this.seamLoopingHandler.unmount();

    if (Shopify.designMode && this.shopifyDesignModelHandler)
      this.shopifyDesignModelHandler.unmount();
  }

  /**
   * Update slide metrics and related static data.
   * Including total slides, single slide width, viewport width, total content width, items per view, etc.
   */
  updateSliderMetrics() {
    this.sliderItems = Array.from(
      this.sliderContainer.querySelectorAll('[id^="Slide-"]'),
    ).filter(
      (slide) => slide.innerHTML.trim() !== "" && slide.offsetParent !== null,
    );

    this.slideLength = this.sliderItems.length;
    if (this.slideLength <= 1) {
      this.slidesPerPage = 1;
      this.sliderItemOffset = 0;
      this.sliderClientSize = 0;
      this.sliderTotalSize = 0;
      this.maxTranslateValue = 0;
      this.totalPages = 1;
      return;
    }

    // Get dimension and count data
    // getBoundingClientRect provides higher precision than offsetLeft
    let sliderContainerRect = this.sliderContainer.getBoundingClientRect();
    const sliderRect0 = this.sliderItems[0].getBoundingClientRect();
    const sliderRect1 = this.sliderItems[1].getBoundingClientRect();
    const sliderRectLast = this.sliderItems.at(-1).getBoundingClientRect();

    // Auto-detect horizontal or vertical slider layout
    this.ifLayoutVertical = false;
    if (sliderRect1.top > sliderRect0.top) {
      this.sliderItemOffset = sliderRect1.top - sliderRect0.top;
      this.sliderClientSize = sliderContainerRect.height;
      this.sliderTotalSize = sliderRectLast.bottom - sliderRect0.top;

      this.ifLayoutVertical = true;
    } else {
      this.sliderClientSize = sliderContainerRect.width;
      if (webvista.isRTL()) {
        // RTL layout
        this.sliderItemOffset = sliderRect0.left - sliderRect1.left;
        this.sliderTotalSize = sliderRect0.right - sliderRectLast.left;
      } else {
        this.sliderItemOffset = sliderRect1.left - sliderRect0.left;
        this.sliderTotalSize = sliderRectLast.right - sliderRect0.left;
      }
    }
    if (this.sliderTotalSize <= this.sliderClientSize) {
      this.slidesPerPage = this.slideLength;
      this.maxTranslateValue = 0;
      this.totalPages = 1;
      return;
    }

    this.slidesPerPage = Math.max(
      1,
      Math.round(this.sliderClientSize / this.sliderItemOffset),
    );
    if (this.slideLength === this.slidesPerPage) this.slidesPerPage--; // Prevent single-page with last item hidden; force two pages
    this.maxTranslateValue = this.sliderTotalSize - this.sliderClientSize;
    this.totalPages = this.slideLength - this.slidesPerPage + 1;
  }

  // Slide by direction
  slideByDirection(direction = "next") {
    if (!this.initSliderStatus) return;
    this.index += direction === "previous" ? -1 : 1;

    this.performSlide();
  }

  /**
   * Scroll to a specific slide element
   * @param element
   * @param silent Whether to use silent mode (no animation)
   * @returns {boolean}
   */
  slideByElement(element = null, silent = false) {
    if (!this.initSliderStatus || !element) return false;

    // If element is a clone, find the original element
    if (element.hasAttribute("data-clone-from"))
      element = this.querySelector(element.getAttribute("data-clone-from"));
    if (!element) return false;

    const elementIndex = this.sliderItems.indexOf(element);
    if (elementIndex >= 0) {
      this.index = elementIndex;
      if (silent) this.disableTransition(); // Disable animation
      this.performSlide();
      if (silent)
        setTimeout(this.enableTransition.bind(this), this.TRANSITION_DURING); // Restore animation
      return true;
    } else {
      return false;
    }
  }

  /**
   * Slide to a specific page
   * @param page Page number
   * @param silent Whether to use silent mode (no animation)
   */
  slideByPage(page = 1, silent = false) {
    if (!this.initSliderStatus || page < 1) return false;

    const slide = this.sliderItems.find(
      (slide) =>
        !slide.hasAttribute("data-clone-from") &&
        parseInt(slide.dataset.page, 10) === page,
    );
    this.slideByElement(slide, silent);
  }

  /**
   * Slide to element by ID (with #, e.g., #Slide-1)
   * @param id
   * @param silent Whether to use silent mode (no animation)
   */
  slideById(id, silent = false) {
    if (!this.initSliderStatus || id == null || id === "") return false;

    const slide = this.querySelector(id);
    if (!slide) return false;

    this.slideByElement(slide, silent);
  }

  /**
   * Slide by offset distance
   * @param offset
   */
  slideByOffset(offset = 0) {
    this.currentTranslate += offset;

    this.applyTranslation();
    this.updateInfo(); // Update display info
  }

  /**
   * Scroll a slide into the visible area
   * @param index
   */
  slideToVisible(index) {
    const slide = this.sliderItems[index];
    if (!slide) return;

    if (this.isVisibleSlide(slide)) return;

    const slideRect = slide.getBoundingClientRect();
    const containerRect = this.sliderContainer.getBoundingClientRect();

    let offset;

    if (webvista.isRTL()) {
      // RTL: left overflow -> scroll right (negative offset), right overflow -> scroll left
      if (slideRect.left <= containerRect.left) {
        offset = containerRect.left - slideRect.left;
      } else {
        offset = containerRect.right - slideRect.right;
      }
    } else {
      if (slideRect.right >= containerRect.right) {
        offset = slideRect.right - containerRect.right;
      } else {
        offset = slideRect.left - containerRect.left;
      }
    }

    this.slideByOffset(offset);
  }

  /**
   * Perform slide transition
   */
  performSlide() {
    this.correctIndex(); // Correct index

    this.updateCurrentTranslate();
    this.applyTranslation();

    this.updateInfo(); // Update display info
  }

  /**
   * Update current translation value
   */
  updateCurrentTranslate() {
    this.currentTranslate = this.index * this.sliderItemOffset;
    if (this.ifLoopingInfinite) return;

    // Handle case where viewport is not an integer multiple of slide items
    if (this.index <= 0) {
      this.currentTranslate = 0;
    } else if (this.index >= this.totalPages - 1) {
      this.currentTranslate = this.maxTranslateValue;
    }
  }

  /**
   * Apply translation transform
   * Can be used independently without changing the index
   */
  applyTranslation() {
    this.correctTranslate();
    this.sliderWrapper.style.transform = this.ifLayoutVertical
      ? `translate3d(0, ${-this.currentTranslate}px, 0)`
      : webvista.isRTL()
        ? `translate3d(${this.currentTranslate}px, 0, 0)`
        : `translate3d(${-this.currentTranslate}px, 0, 0)`;
  }

  /**
   * Dispatch slider change event
   */
  dispatchChangeEvent() {
    this.dispatchEvent(
      new CustomEvent("sliderChanged", {
        bubbles: true,
        detail: {
          currentIndex: this.index,
          currentElement: this.sliderItems[this.index],
        },
      }),
    );
  }

  // Correct index
  correctIndex() {
    if (this.ifEnableLooping) {
      if (this.index < 0) {
        this.index = this.slideLength - this.slidesPerPage;
      } else if (this.index > this.slideLength - this.slidesPerPage) {
        this.index = 0;
      }
    } else {
      if (this.index < 0) {
        this.index = 0;
      } else if (this.index > this.slideLength - this.slidesPerPage) {
        this.index = this.slideLength - this.slidesPerPage;
      }
    }
  }

  /**
   * Correct translation distance
   */
  correctTranslate() {
    // Drag threshold with elastic effect, only active during dragging
    let threshold = 0;
    if (this.dragHandler && this.dragHandler.isDragging)
      threshold = Math.min(
        this.sliderItemOffset / 2,
        this.TRANSITION_THRESHOLD,
      );
    const boundary = [-threshold, this.maxTranslateValue + threshold];

    if (this.currentTranslate > boundary[1]) {
      this.currentTranslate = boundary[1];
      this.setAttribute("data-move-exceed", "next");
    } else if (this.currentTranslate < boundary[0]) {
      this.currentTranslate = boundary[0];
      this.setAttribute("data-move-exceed", "pre");
    } else {
      this.removeAttribute("data-move-exceed");
    }
  }

  /**
   * Update slider state info
   */
  updateInfo() {
    // Activate current slide
    if (this.currentSlide) {
      this.currentSlide.classList.remove("active");
    }
    this.currentSlide = this.sliderItems[this.index];
    if (this.currentSlide) {
      this.currentSlide.classList.add("active");
      if (this.ifAriaLive) {
        // Remove aria-hidden from visible slides, hide the rest
        this.sliderItems.forEach((slide, i) => {
          if (i >= this.index && i < this.index + this.slidesPerPage) {
            slide.removeAttribute("aria-hidden");
          } else {
            slide.setAttribute("aria-hidden", "true");
          }
        });
      }
    }

    // Pause currently playing media
    if (!this.keepVideo) {
      webvista.pauseAllMedia(this);

      // Delay to ensure video is fully initialized
      if (this._playMediaTimer) clearTimeout(this._playMediaTimer);
      this._playMediaTimer = setTimeout(() => {
        webvista.playAllMedia(this.currentSlide);
        this._playMediaTimer = null;
      }, 50);
    }

    // Dispatch slideChanged event (clear queued timer to prevent event buildup during rapid paging)
    if (this._changeEventTimer) clearTimeout(this._changeEventTimer);
    this._changeEventTimer = setTimeout(
      this.dispatchChangeEvent.bind(this),
      this.TRANSITION_DURING,
    );

    if (this.autoplayHandler && !this.autoplayHandler.ifPaused) this.autoplayHandler.play(true); // Play

    // Update basic module info
    if (this.BasicHandler) this.BasicHandler.updateInfo(this.currentSlide, this.totalPages);
  }

  /**
   * Check if an element is visible within the current viewport
   * @param element
   * @returns {boolean}
   */
  isVisibleSlide(element = null) {
    if (!element) return false;

    const elementIndex = this.sliderItems.indexOf(element);
    // If element is not in sliderItems, return false
    if (elementIndex === -1) return false;

    return (
      elementIndex >= this.index &&
      elementIndex < this.index + this.slidesPerPage
    );
  }

  /**
   * Check if element is the current slide
   * @param element
   * @returns {boolean}
   */
  isCurrentSlide(element = null) {
    return element === this.sliderItems[this.index];
  }

  // Disable transition
  disableTransition() {
    this.sliderWrapper.style.transition = "none";
  }

  // Enable transition
  enableTransition() {
    this.sliderWrapper.style.transition = this.ifSlideSmooth
      ? `transform ${this.TRANSITION_DURING}ms`
      : '';
  }

  /**
   * Clear slider transform
   */
  clearTranslation() {
    this.index = 0;
    this.preTranslate = 0;
    this.currentTranslate = 0;
    if (this.sliderWrapper) {
      this.sliderWrapper.style.transition = "";
      this.sliderWrapper.style.transform = "";
    }
  }
}

customElements.define("slider-component", Slider);

/**
 * Product detail media slider
 */
class ProductMediaSlider extends Slider {}

customElements.define("product-media-slider", ProductMediaSlider);

/**
 * Thumbnail slider
 */
class ProductThumbnailSlider extends Slider {
  /**
   * Move a thumbnail element to the optimal visible position
   * @param element
   * @returns {boolean}
   */
  moveElementToOptimalPosition(element) {
    if (
      !this.initSliderStatus ||
      !element ||
      this.sliderItems.indexOf(element) < 0
    )
      return false;

    this.index = this.sliderItems.indexOf(element);
    // Adjust index to the optimal position
    if (
      this.index < 0 ||
      (this.index > this.slideLength - this.slidesPerPage &&
        this.index < this.slideLength)
    ) {
      this.index = this.totalPages - 1; // Last page
    } else if (this.index >= this.slideLength) {
      this.index = 0;
    } else if (this.index > 0) {
      this.index = this.index - 1;
    }

    this.performSlide();
  }
}
customElements.define("product-thumbnail-slider", ProductThumbnailSlider);

